/**
 * EditAccountDialog — view + edit a connected mailbox.
 *
 * All accounts: rename, make-primary. Generic (IMAP) accounts: edit the
 * IMAP/SMTP host/port/security and re-enter credentials (both re-probed
 * server-side before persisting). OAuth accounts (Gmail/Outlook): transport +
 * credentials are provider-managed, so those show a "Reconnect" action that
 * re-runs the OAuth flow (the callback refreshes the existing account).
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Star } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import type { EmailConnection } from '@/lib/api';
import {
  useUpdateEmailConnection,
  useUpdateEmailTransport,
  useUpdateEmailCredentials,
  useInitEmailOAuth,
} from '../hooks/useConnectAccount';

type Security = 'ssl_tls' | 'starttls' | 'none';
const SECURITY_OPTIONS: { value: Security; label: string }[] = [
  { value: 'ssl_tls', label: 'SSL/TLS' },
  { value: 'starttls', label: 'STARTTLS' },
  { value: 'none', label: 'None' },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connection: EmailConnection | null;
}

export function EditAccountDialog({ open, onOpenChange, connection }: Props) {
  const update = useUpdateEmailConnection();
  const updateTransport = useUpdateEmailTransport();
  const updateCredentials = useUpdateEmailCredentials();
  const reconnect = useInitEmailOAuth();

  const isGeneric = connection?.provider === 'generic';

  const [displayName, setDisplayName] = useState('');
  // Transport fields (generic accounts only).
  // Protocol is no longer user-editable (POP3 deferred from the UI); we still
  // seed + resubmit the account's existing value unchanged. See docs/api-email.md.
  const [incomingProtocol, setIncomingProtocol] = useState<'imap' | 'pop3'>('imap');
  const [incomingHost, setIncomingHost] = useState('');
  const [incomingPort, setIncomingPort] = useState('993');
  const [incomingSecurity, setIncomingSecurity] = useState<Security>('ssl_tls');
  const [outgoingHost, setOutgoingHost] = useState('');
  const [outgoingPort, setOutgoingPort] = useState('465');
  const [outgoingSecurity, setOutgoingSecurity] = useState<Security>('ssl_tls');
  // New credentials (generic accounts only).
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (!open || !connection) return;
    setDisplayName(connection.displayName ?? '');
    setIncomingProtocol(connection.incomingProtocol ?? 'imap');
    setIncomingHost(connection.incomingHost ?? '');
    setIncomingPort(connection.incomingPort != null ? String(connection.incomingPort) : '993');
    setIncomingSecurity(connection.incomingSecurity ?? 'ssl_tls');
    setOutgoingHost(connection.outgoingHost ?? '');
    setOutgoingPort(connection.outgoingPort != null ? String(connection.outgoingPort) : '465');
    setOutgoingSecurity(connection.outgoingSecurity ?? 'ssl_tls');
    setUsername('');
    setPassword('');
    update.reset();
    updateTransport.reset();
    updateCredentials.reset();
    // Seed only on open / account switch.
  }, [open, connection?.accountId]);

  if (!connection) return null;

  const saveDetails = () => {
    update.mutate(
      { accountId: connection.accountId, input: { displayName: displayName.trim() || null } },
      { onSuccess: () => toast({ description: 'Account updated' }) },
    );
  };

  const makePrimary = () => {
    update.mutate(
      { accountId: connection.accountId, input: { makePrimary: true } },
      { onSuccess: () => toast({ description: 'Set as primary mailbox' }) },
    );
  };

  const saveTransport = () => {
    updateTransport.mutate(
      {
        accountId: connection.accountId,
        input: {
          incomingProtocol,
          incomingHost: incomingHost.trim(),
          incomingPort: Number(incomingPort),
          incomingSecurity,
          outgoingHost: outgoingHost.trim(),
          outgoingPort: Number(outgoingPort),
          outgoingSecurity,
        },
      },
      { onSuccess: () => toast({ description: 'Server settings updated' }) },
    );
  };

  const saveCredentials = () => {
    updateCredentials.mutate(
      {
        accountId: connection.accountId,
        input: { username: username.trim() || undefined, password },
      },
      {
        onSuccess: () => {
          toast({ description: 'Password updated' });
          setPassword('');
        },
      },
    );
  };

  const transportError = updateTransport.error as Error | null;
  const credentialsError = updateCredentials.error as Error | null;
  const portValid = (v: string) => Number(v) >= 1 && Number(v) <= 65535;
  const transportValid =
    incomingHost.trim() &&
    outgoingHost.trim() &&
    portValid(incomingPort) &&
    portValid(outgoingPort);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="truncate pr-6">{connection.emailAddress}</DialogTitle>
        </DialogHeader>

        <div className="max-h-[70vh] space-y-5 overflow-y-auto pr-1">
          {/* Display name + primary */}
          <section className="space-y-2">
            <div className="space-y-1">
              <Label htmlFor="edit-display-name">Display name</Label>
              <div className="flex gap-2">
                <Input
                  id="edit-display-name"
                  value={displayName}
                  placeholder={connection.emailAddress}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
                <Button onClick={saveDetails} disabled={update.isPending} className="shrink-0">
                  {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
            </div>
            {connection.isPrimary ? (
              <p className="flex items-center gap-1 text-xs text-muted-foreground">
                <Star className="h-3 w-3 fill-current text-yellow-500" /> Primary mailbox
              </p>
            ) : (
              <Button variant="outline" size="sm" className="gap-1" onClick={makePrimary}>
                <Star className="h-3.5 w-3.5" /> Make primary
              </Button>
            )}
          </section>

          {isGeneric ? (
            <>
              {/* Transport */}
              <section className="space-y-2 border-t pt-4">
                <h3 className="text-sm font-medium">Incoming & outgoing server</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Incoming security</Label>
                    <select
                      className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                      value={incomingSecurity}
                      onChange={(e) => setIncomingSecurity(e.target.value as Security)}
                    >
                      {SECURITY_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="in-host">Incoming host</Label>
                    <Input
                      id="in-host"
                      value={incomingHost}
                      onChange={(e) => setIncomingHost(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="in-port">Incoming port</Label>
                    <Input
                      id="in-port"
                      inputMode="numeric"
                      value={incomingPort}
                      onChange={(e) => setIncomingPort(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="out-host">Outgoing (SMTP) host</Label>
                    <Input
                      id="out-host"
                      value={outgoingHost}
                      onChange={(e) => setOutgoingHost(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="out-port">Outgoing port</Label>
                    <Input
                      id="out-port"
                      inputMode="numeric"
                      value={outgoingPort}
                      onChange={(e) => setOutgoingPort(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Outgoing security</Label>
                    <select
                      className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                      value={outgoingSecurity}
                      onChange={(e) => setOutgoingSecurity(e.target.value as Security)}
                    >
                      {SECURITY_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {transportError && (
                  <p className="text-xs text-destructive">{transportError.message}</p>
                )}
                <Button
                  variant="outline"
                  onClick={saveTransport}
                  disabled={!transportValid || updateTransport.isPending}
                >
                  {updateTransport.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Test & save server settings'
                  )}
                </Button>
              </section>

              {/* Credentials */}
              <section className="space-y-2 border-t pt-4">
                <h3 className="text-sm font-medium">Update credentials</h3>
                <p className="text-xs text-muted-foreground">
                  Re-enter your password (or app password). Leave the username blank to keep it.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="cred-user">Username</Label>
                    <Input
                      id="cred-user"
                      autoComplete="off"
                      value={username}
                      placeholder="(unchanged)"
                      onChange={(e) => setUsername(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="cred-pass">Password</Label>
                    <Input
                      id="cred-pass"
                      type="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                </div>
                {credentialsError && (
                  <p className="text-xs text-destructive">{credentialsError.message}</p>
                )}
                <Button
                  variant="outline"
                  onClick={saveCredentials}
                  disabled={!password || updateCredentials.isPending}
                >
                  {updateCredentials.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Test & update password'
                  )}
                </Button>
              </section>
            </>
          ) : (
            <section className="space-y-2 border-t pt-4">
              <h3 className="text-sm font-medium">Connection</h3>
              <p className="text-xs text-muted-foreground">
                {connection.provider === 'gmail' ? 'Gmail' : 'Outlook'} is connected via OAuth — the
                server settings are managed by the provider. Reconnect to refresh access if syncing
                stops working.
              </p>
              <Button
                variant="outline"
                disabled={reconnect.isPending}
                onClick={() =>
                  reconnect.mutate(connection.provider === 'gmail' ? 'gmail' : 'microsoft')
                }
              >
                {reconnect.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reconnect'}
              </Button>
            </section>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
